/** @format */

import React, { useState, useEffect } from "react";
import moment from "moment";
import { saveAs } from "file-saver";
import { MdOutlineFileDownload } from "react-icons/md";
import { CiSearch } from "react-icons/ci";
import Cookies from "js-cookie";
import Select from "react-select";
import { motion } from "framer-motion";

const Absence_Report = ({ selectedDate }) => {
  const [absenteeData, setAbsenteeData] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [filterDate, setFilterDate] = useState(
    selectedDate || moment().format("YYYY-MM-DD")
  );
  const [search, setSearch] = useState(""); // 🔍 universal employee search
  const [departmentDesignationId, setDepartmentDesignationId] = useState("");
  const [workingOffice, setWorkingOffice] = useState("");
  const [organizationOptions, setOrganizationOptions] = useState([]);
  const [isExporting, setIsExporting] = useState(false);
  const [selectedOrganizationFilter, setSelectedOrganizationFilter] =
    useState("");
  const rowsPerPage = 10;
  const API_URL = process.env.REACT_APP_FRONTEND_URL;
  const token = Cookies.get("user_token");
  const [departments, setDepartments] = useState([]);
  const [workingOffices, setWorkingOffices] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [selectedWorkingOffice, setSelectedWorkingOffice] = useState("");
  const [triggerSearch, setTriggerSearch] = useState(false);

  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        const authToken = Cookies.get("user_token");
        const res = await fetch(
          `${API_URL}/v1/hris/organizations/organization`,
          {
            headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
            credentials: "include",
          }
        );

        const json = await res.json();
        if (json.success && Array.isArray(json.data)) {
          setOrganizationOptions(
            json.data.map((org) => ({
              value: org.id,
              label: org.organization_name,
            }))
          );
        }
      } catch (err) {
        console.error("Failed to fetch organizations", err);
      }
    };

    fetchOrganizations();
  }, [API_URL]);

  // 🔹 Fetch designations (with departments)
  useEffect(() => {
    const fetchDesignations = async () => {
      try {
        const res = await fetch(`${API_URL}/v1/hris/designation`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (data.success) setDesignations(data.data || []);
      } catch (err) {
        console.error("Error fetching designations:", err);
      }
    };
    fetchDesignations();
  }, [API_URL, token]);

  // 🔹 Fetch absentee data
  useEffect(() => {
    const fetchAbsenteeData = async () => {
      try {
        setLoading(true);
        setError(null);

        const params = new URLSearchParams({
          date: moment(filterDate).format("YYYY-MM-DD"),
          page: currentPage,
          limit: rowsPerPage,
        });

        if (search) params.append("search", search);
        if (selectedDepartment) params.append("department_designation_id", selectedDepartment);
        if (selectedWorkingOffice) params.append("working_office", selectedWorkingOffice);
        if (selectedOrganizationFilter) params.append("organization", selectedOrganizationFilter);

        const response = await fetch(
          `${API_URL}/v1/hris/new-attendence/absentees?${params.toString()}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        setAbsenteeData(result.data || []);
        setTotalPages(result.totalPages || 0);
        setTotalRecords(result.total || 0);
      } catch (err) {
        setError(err.message);
        setAbsenteeData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAbsenteeData();
  }, [triggerSearch, currentPage]);


  // 🔹 Avatar background color
  const avatarBgClass = (seed = "") => {
    const palette = [
      "bg-sky-500",
      "bg-indigo-500",
      "bg-emerald-500",
      "bg-rose-500",
      "bg-amber-500",
      "bg-teal-500",
      "bg-fuchsia-500",
      "bg-cyan-500",
    ];
    let hash = 0;
    for (let i = 0; i < seed.length; i++)
      hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
    return palette[hash % palette.length];
  };

  // 🔹 Get initials for avatar
  const getInitials = (fullName = "") => {
    const tokens = String(fullName)
      .replace(/[^\p{L}\p{N}\s'-]/gu, "")
      .trim()
      .split(/\s+/)
      .filter(Boolean);
    if (tokens.length === 0) return "??";
    if (tokens.length === 1) return tokens[0].slice(0, 2).toUpperCase();
    const first = tokens[0][0] || "";
    const last = tokens[tokens.length - 1][0] || "";
    return (first + last).toUpperCase();
  };

  const getDesignationTitle = (id) => {
    const des = designations.find((d) => d.id === Number(id));
    if (!des) return id || "N/A";
    return `${des.Department?.name || "N/A"} - ${des.title}`;
  };

  // 🔹 CSV Export
  const exportToCSV = async () => {
    if (totalRecords === 0) {
      alert("No data available to export.");
      return;
    }

    setIsExporting(true);
    try {
      const params = new URLSearchParams({
        date: moment(filterDate).format("YYYY-MM-DD"),
      });
      if (search) params.append("search", search); // 🔍 universal search
      if (departmentDesignationId)
        params.append("designation_title", departmentDesignationId);
      if (workingOffice) params.append("working_office", workingOffice);
      if (selectedOrganizationFilter)
        params.append("organization_id", selectedOrganizationFilter);
      const response = await fetch(
        `${API_URL}/v1/hris/new-attendence/absentees?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.ok)
        throw new Error("Failed to fetch full data for export.");

      const result = await response.json();
      const allData = result.data || [];
      if (allData.length === 0) {
        alert("No data found for the selected filters.");
        return;
      }

      const headers = [
        "Employee No",
        "Employee Name",
        "Calling Name",
        "Dept/Designation",
        "Working Office",
        "Branch",
      ];
      const csvRows = [headers.join(",")];

      for (const row of allData) {
        const values = [
          row.employee_no,
          row.employee_fullname,
          row.employee_calling_name,
          getDesignationTitle(row.designation_title),
          row.working_office_name,
          row.branch_name,
        ].map((value) => `"${String(value || "N/A").replace(/"/g, '""')}"`);
        csvRows.push(values.join(","));
      }

      const csvString = csvRows.join("\n");
      const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
      saveAs(blob, `absentee_report_${moment().format("YYYY-MM-DD")}.csv`);
    } catch (err) {
      console.error("CSV Export Error:", err);
      alert("An error occurred while exporting the data.");
    } finally {
      setIsExporting(false);
    }
  };

  // 🔹 Fetch Departments when organization changes
  useEffect(() => {
    const fetchDepartments = async () => {
      if (!selectedOrganizationFilter) {
        setDepartments([]);
        return;
      }
      try {
        const res = await fetch(
          `${API_URL}/v1/hris/organizations/departments?organization_id=${selectedOrganizationFilter}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const data = await res.json();
        if (data.success) {
          setDepartments(
            data.data.map((d) => ({
              value: d.id,
              label: d.name,
            }))
          );
        } else {
          setDepartments([]);
        }
      } catch (err) {
        console.error("Failed to fetch departments:", err);
        setDepartments([]);
      }
    };
    fetchDepartments();
  }, [API_URL, token, selectedOrganizationFilter]);

  // 🔹 Fetch Working Offices when organization changes
  useEffect(() => {
    const fetchWorkingOffices = async () => {
      if (!selectedOrganizationFilter) {
        setWorkingOffices([]);
        return;
      }
      try {
        const res = await fetch(
          `${API_URL}/v1/hris/organizations/working-offices?organization_id=${selectedOrganizationFilter}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const data = await res.json();
        if (data.success) {
          setWorkingOffices(
            data.data.map((w) => ({
              value: w.id,
              label: w.name,
            }))
          );
        } else {
          setWorkingOffices([]);
        }
      } catch (err) {
        console.error("Failed to fetch working offices:", err);
        setWorkingOffices([]);
      }
    };
    fetchWorkingOffices();
  }, [API_URL, token, selectedOrganizationFilter]);

  return (
    <motion.div
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -50 }}
      transition={{ duration: 0.4 }}
    >
      <div className="mt-5 overflow-y-auto font-montserrat">
        <p className="text-[25px]">Absence Report</p>

        {/* Filters */}
        <div className=" rounded-xl">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div>
              <label className="text-sm font-medium text-gray-700">Date</label>
              <input
                type="date"
                value={filterDate}
                onChange={(e) => setFilterDate(e.target.value)}
                className="w-full mt-1 border border-gray-300 rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* 🔍 Universal search input */}
            <div className="relative self-end">
              <CiSearch className="absolute left-3 top-3 text-gray-400" />
              <input
                type="text"
                placeholder="Search (No, Name, Email...)"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setCurrentPage(1);
                }}
                className="pl-10 border border-gray-300 rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 w-full"
              />
            </div>


            <div className="w-full mt-8">
              <Select
                options={organizationOptions}
                placeholder="Select Organization"
                value={
                  organizationOptions.find(
                    (opt) => opt.value === Number(selectedOrganizationFilter)
                  ) || null
                }
                onChange={(opt) => {
                  setSelectedOrganizationFilter(opt ? opt.value : "");
                  setCurrentPage(1); // Reset page on organization change
                }}
                isClearable
                className="basic-single"
                classNamePrefix="select"
              />
            </div>

            {/* Department Dropdown */}
            <div className="self-end">
              <Select
                options={departments}
                placeholder="Select Department"
                value={departments.find((opt) => opt.value === selectedDepartment) || null}
                onChange={(opt) => {
                  setSelectedDepartment(opt ? opt.value : "");
                  setCurrentPage(1);
                }}
                isClearable
                isDisabled={!selectedOrganizationFilter}
              />
            </div>

            {/* Working Office Dropdown */}
            <div className="self-end">
              <Select
                options={workingOffices}
                placeholder="Select Working Office"
                value={
                  workingOffices.find((opt) => opt.value === selectedWorkingOffice) ||
                  null
                }
                onChange={(opt) => {
                  setSelectedWorkingOffice(opt ? opt.value : "");
                  setCurrentPage(1);
                }}
                isClearable
                isDisabled={!selectedOrganizationFilter}
              />
            </div>
            <div className="self-end">
              <button
                onClick={() => {
                  setCurrentPage(1);
                  setTriggerSearch((prev) => !prev); // 🔄 toggle to trigger data fetch
                }}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-all"
              >
                Search
              </button>
            </div>
            <div className="self-end">
              <button
                onClick={() => {
                  setSelectedDepartment("");
                  setSelectedWorkingOffice("");
                  setSelectedOrganizationFilter("");
                  setSearch("");
                  setFilterDate(moment().format("YYYY-MM-DD"));
                  setCurrentPage(1);
                  setTriggerSearch((prev) => !prev); // refresh data
                }}
                className="w-full px-4 py-2 border border-gray-400 text-gray-700 rounded hover:bg-gray-100 transition-all"
              >
                Reset
              </button>
            </div>
          </div>

          {/* Export Button */}
          <div className="flex justify-start mb-4">
            <button
              onClick={exportToCSV}
              disabled={isExporting}
              className="px-4 py-2 text-white bg-blue-500 rounded hover:bg-blue-600 flex items-center gap-2 disabled:opacity-50"
            >
              <MdOutlineFileDownload size={20} />
              <span>{isExporting ? "Exporting..." : "Export CSV"}</span>
            </button>
          </div>




          {/* Table */}
          <div className="overflow-x-auto bg-white shadow p-2 rounded-xl">
            <table className="w-full text-sm text-left">
              <thead className="bg-white text-gray-600 border-b border-t">
                <tr>
                  <th className="px-6 py-4">Employee No</th>
                  <th className="px-6 py-4">Employee Name</th>
                  <th className="px-6 py-4">Calling Name</th>
                  <th className="px-6 py-4">Dept / Designation</th>
                  <th className="px-6 py-4">Working Office</th>
                  <th className="px-6 py-4">Branch</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 border-t border-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan="6" className="text-center py-4 text-gray-500">
                      Loading...
                    </td>
                  </tr>
                ) : error ? (
                  <tr>
                    <td colSpan="6" className="text-center py-4 text-red-500">
                      Error: {error}
                    </td>
                  </tr>
                ) : absenteeData.length > 0 ? (
                  absenteeData.map((row) => (
                    <tr key={row.id} className="hover:bg-gray-50">
                      <td className="p-3 text-blue-600">{row.employee_no}</td>
                      <td className="p-3">
                        <div className="flex items-center gap-3">
                          <div
                            className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs shrink-0 ${avatarBgClass(
                              row.employee_fullname || row.employee_no
                            )}`}
                            title={row.employee_fullname}
                          >
                            {getInitials(
                              row.employee_fullname || row.employee_no
                            )}
                          </div>
                          <div>
                            <div className="font-semibold leading-5">
                              {row.employee_fullname}
                            </div>
                            <div className="text-xs text-gray-500">
                              EMP ID: {row.employee_no}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="p-3">
                        {row.employee_calling_name || "N/A"}
                      </td>
                      <td className="p-3">
                        {getDesignationTitle(row.designation_title)}
                      </td>
                      <td className="p-3">{row.working_office_name || "N/A"}</td>
                      <td className="p-3">{row.branch_name || "N/A"}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="text-center py-4 text-gray-500">
                      No data found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex justify-between items-center mt-4">
            <div>
              <span className="text-sm text-gray-700">
                Showing{" "}
                {totalRecords > 0 ? (currentPage - 1) * rowsPerPage + 1 : 0} to{" "}
                {Math.min(currentPage * rowsPerPage, totalRecords)} of{" "}
                {totalRecords} results
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1 || loading}
                className="px-4 py-2 text-sm bg-gray-200 rounded disabled:opacity-50"
              >
                Previous
              </button>
              <span className="text-sm text-gray-700">
                Page {currentPage} of {totalPages > 0 ? totalPages : 1}
              </span>
              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={
                  currentPage === totalPages || totalPages === 0 || loading
                }
                className="px-4 py-2 text-sm bg-gray-200 rounded disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default Absence_Report;
